// @flow
import * as React from 'react';
import {useState} from 'react';

import {useArticle, usePrefs, useLocation} from 'docs/data';
// import {useActivePage} from 'docs/hooks';
import {AlignBox} from 'sail/Align';
import Card from 'sail/Card';
import FormLayout, {FormBlock, FormRow} from 'sail/FormLayout';
import Box from 'sail/Box';
import Group from 'sail/Group';
import List, {ListItem} from 'sail/List';
import Radio from 'sail/Radio';
import {Heading, BodyTextProps, Text, TitleTextProps} from 'sail/Text';
import {InfoTooltip} from 'sail/Tooltip';
import Select, {SelectOption} from 'sail/Select';
import {motion} from 'framer-motion';
import './ConnectPaymentIntegrationPageSelector.scss';
import {removeNulls} from 'docs/util';

import {defineMessages, useIntl} from 'react-intl';

import {
  useContentAnimation,
  variants,
  transition,
} from 'docs/shell/content/ContentAnimationProvider';

const messages = defineMessages({
  directTooltipHeading: {
    id: 'docsApp.connect.automaticPayments.tooltipHeading.direct',
    description: 'Tooltip heading for direct charges.',
    defaultMessage: 'Direct: ',
  },
  directTooltip: {
    id: 'docsApp.connect.automaticPayments.tooltip.direct',
    description: 'Tooltip describing direct charges.',
    defaultMessage: 'Single user charge directly to your connected account.',
  },
  onBehalfOfTooltipHeading: {
    id: 'docsApp.connect.automaticPayments.tooltipHeading.onBehalfOf',
    description: 'Tooltip heading for on_behalf_of parameter.',
    defaultMessage: 'With on_behalf_of parameter: ',
  },
  onBehalfOfTooltip: {
    id: 'docsApp.connect.automaticPayments.tooltip.onBehalfOf',
    description: 'Tooltip describing on_behalf_of parameter.',
    defaultMessage:
      'A destination charge or separate charge and transfer with the on_behalf_of paremeter defined in its schema.',
  },
  notOnBehalfOfTooltipHeading: {
    id: 'docsApp.connect.automaticPayments.tooltipHeading.notOnBehalfOf',
    description: 'Tooltip heading for not on_behalf_of parameter.',
    defaultMessage: 'Without on_behalf_of parameter: ',
  },
  notOnBehalfOfTooltip: {
    id: 'docsApp.connect.automaticPayments.tooltip.notOnBehalfOf',
    description: 'Tooltip describing not on_behalf_of parameter.',
    defaultMessage:
      'A destination charge or separate charge and transfer without the on_behalf_of parameter defined in its schema.',
  },
  standardAccountTooltipHeading: {
    id: 'docsApp.connect.automaticPayments.tooltipHeading.standardAccount',
    description: 'Tooltip heading for Standard account.',
    defaultMessage: 'Standard: ',
  },
  standardAccountTooltip: {
    id: 'docsApp.connect.automaticPayments.tooltip.standardAccount',
    description: 'Tooltip describing Standard account.',
    defaultMessage:
      'The account holder (your platform’s user) has a relationship with Stripe, is able to log in to the Dashboard, and can process charges on their own.',
  },
  customAccountTooltipHeading: {
    id: 'docsApp.connect.automaticPayments.tooltipHeading.customAccount',
    description: 'Tooltip heading for Custom account.',
    defaultMessage: 'Custom: ',
  },
  customAccountTooltip: {
    id: 'docsApp.connect.automaticPayments.tooltip.customAccount',
    description: 'Tooltip describing Custom account.',
    defaultMessage:
      'You, the platform, are responsible for all interactions with your users and for collecting all the information needed to verify the account.',
  },
  expressAccountTooltipHeading: {
    id: 'docsApp.connect.automaticPayments.tooltipHeading.expressAccount',
    description: 'Tooltip heading for Express account.',
    defaultMessage: 'Express: ',
  },
  expressAccountTooltip: {
    id: 'docsApp.connect.automaticPayments.tooltip.expressAccount',
    description: 'Tooltip describing Express account.',
    defaultMessage:
      'Stripe handles the onboarding and identity verification processes. The platform has the ability to specify charge types and set the connected account’s payout settings programmatically. The platform is responsible for handling disputes and refunds.',
  },
  formTitleHeading: {
    id: 'docsApp.connect.automaticPayments.form.heading',
    description: 'Heading of form selector.',
    defaultMessage: 'Confirm your desired integration',
  },
  radioLabelCheckout: {
    id: 'docsApp.connect.automaticPayments.form.radioLabel.checkout',
    description: 'Label for Checkout radio option.',
    defaultMessage: 'Stripe Checkout',
  },
  radioDescriptionCheckout: {
    id: 'docsApp.connect.automaticPayments.form.radioDescription.checkout',
    description: 'Description for Checkout radio option.',
    defaultMessage: 'Checkout is a Stripe-hosted payment form.',
  },
  radioLabelPaymentElement: {
    id: 'docsApp.connect.automaticPayments.form.radioLabel.paymentElement',
    description: 'Label for Payment Element radio option.',
    defaultMessage: 'Payment Element',
  },
  radioDescriptionPaymentElement: {
    id:
      'docsApp.connect.automaticPayments.form.radioDescription.paymentElement',
    description: 'Description for Payment Element radio option.',
    defaultMessage:
      'Payment Element (for web and mobile) is a UI component that you embed into your website or app.',
  },
  radioLabelApi: {
    id: 'docsApp.connect.automaticPayments.form.radioLabel.api',
    description: 'Label for API radio option.',
    defaultMessage: 'API only',
  },
  radioDescriptionApi: {
    id: 'docsApp.connect.automaticPayments.form.radioDescription.api',
    description: 'Description for API radio option.',
    defaultMessage:
      'Custom checkout integrations that leverage the Stripe API.',
  },
  selectOptionLabelDirectCharge: {
    id: 'docsApp.connect.automaticPayments.form.selectOptionDirectCharge',
    description: 'Select option for direct charge.',
    defaultMessage: 'Direct charge',
  },
  selectOptionLabelOnBehalfOf: {
    id: 'docsApp.connect.automaticPayments.form.selectOptionOnBehalfOf',
    description: 'Select option for on_bhalf_of.',
    defaultMessage: 'With on_behalf_of parameter',
  },
  selectOptionLabelNotOnBehalfOf: {
    id: 'docsApp.connect.automaticPayments.form.selectOptionNotOnBehalfOf',
    description: 'Select option for not on_behalf_of.',
    defaultMessage: 'Without on_behalf_of parameter',
  },
  selectOptionLabelStandardAccount: {
    id: 'docsApp.connect.automaticPayments.form.selectOptionStandardAccount',
    description: 'Select option for Standard account.',
    defaultMessage: 'Standard account',
  },
  selectOptionLabelCustomAccount: {
    id: 'docsApp.connect.automaticPayments.form.selectOptionCustomAccount',
    description: 'Select option for Custom account.',
    defaultMessage: 'Custom account',
  },
  selectOptionLabelExpressAccount: {
    id: 'docsApp.connect.automaticPayments.form.selectOptionExpressAccount',
    description: 'Select option for Express account.',
    defaultMessage: 'Express account',
  },
});

// Different sets of selector options may use different sets of prefs. As a result,
// when the user chooses an option, we un-set all page prefs which aren't explicitly
// used in the selector option's prefs.
const buildPagePrefs = (
  pagePrefs: Array<string>,
  values: {[pref: string]: string},
) => {
  return pagePrefs.reduce((hash, pref) => {
    hash[pref] = values[pref] || null;
    return hash;
  }, {});
};

function useUpdatePagePrefs() {
  const {article} = useArticle();
  const {setPrefs} = usePrefs();
  const {replaceLocation} = useLocation();

  return (optionPrefs) => {
    const prefs = buildPagePrefs(article.pagePrefs, optionPrefs);
    setPrefs(prefs);
    replaceLocation({query: removeNulls(prefs)});
  };
}

export const ConnectPaymentIntegrationPageSelector = () => {
  const {direction} = useContentAnimation();
  const {getPref} = usePrefs();
  const updatePrefs = useUpdatePagePrefs();
  const defaultSelections = {
    'connect-payment-integration-type':
      getPref('connect-payment-integration-type') || 'checkout',
    'connect-charge-type': getPref('connect-charge-type') || 'direct',
    'connect-account-type': getPref('connect-account-type') || 'standard',
  };
  const [selections, setSelections] = useState(defaultSelections);
  const intl = useIntl();

  return (
    <Group margin={{top: 20, bottom: 20}} spacing={20}>
      <Card shadow="medium">
        <FormLayout background="offset">
          <FormBlock
            rowDividers
            title={
              <Heading>{intl.formatMessage(messages.formTitleHeading)}</Heading>
            }
          />
          <FormBlock rowDividers>
            <FormRow name="radioRow" label="Payments integration:">
              <Group spacing={12}>
                <AlignBox reference={BodyTextProps}>
                  <Box padding={{right: 16}}>
                    <Radio
                      key="checkout-radio"
                      id="checkout-radio"
                      label={intl.formatMessage(messages.radioLabelCheckout)}
                      description={intl.formatMessage(
                        messages.radioDescriptionCheckout,
                      )}
                      checked={
                        !getPref('connect-payment-integration-type') ||
                        getPref('connect-payment-integration-type') ===
                          'checkout'
                      }
                      onChange={() => {
                        updatePrefs({
                          ...selections,
                          'connect-payment-integration-type': 'checkout',
                        });
                        setSelections({
                          ...selections,
                          'connect-payment-integration-type': 'checkout',
                        });
                      }}
                    />
                  </Box>
                </AlignBox>
                <AlignBox reference={BodyTextProps}>
                  <Box padding={{right: 16}}>
                    <Radio
                      key="payment-radio"
                      id="payment-radio"
                      label={intl.formatMessage(
                        messages.radioLabelPaymentElement,
                      )}
                      description={intl.formatMessage(
                        messages.radioDescriptionPaymentElement,
                      )}
                      checked={
                        getPref('connect-payment-integration-type') ===
                        'payment'
                      }
                      onChange={() => {
                        updatePrefs({
                          ...selections,
                          'connect-payment-integration-type': 'payment',
                        });
                        setSelections({
                          ...selections,
                          'connect-payment-integration-type': 'payment',
                        });
                      }}
                    />
                  </Box>
                </AlignBox>
                <AlignBox reference={BodyTextProps}>
                  <Box padding={{right: 16}}>
                    <Radio
                      key="api-radio"
                      id="api-radio"
                      label={intl.formatMessage(messages.radioLabelApi)}
                      description={intl.formatMessage(
                        messages.radioDescriptionApi,
                      )}
                      checked={
                        getPref('connect-payment-integration-type') === 'api'
                      }
                      onChange={() => {
                        updatePrefs({
                          ...selections,
                          'connect-payment-integration-type': 'api',
                        });
                        setSelections({
                          ...selections,
                          'connect-payment-integration-type': 'api',
                        });
                      }}
                    />
                  </Box>
                </AlignBox>
              </Group>
            </FormRow>
          </FormBlock>
          <FormBlock rowDividers>
            <FormRow name="chargeRow" label="Charge type:">
              <Group direction="horizontal" spacing={12}>
                <Select
                  id="connect-charge-type"
                  width="long"
                  name="connect-charge-type"
                  value={getPref('connect-charge-type') || 'direct'}
                  onChange={(e) => {
                    updatePrefs({...selections, 'connect-charge-type': e});
                    setSelections({...selections, 'connect-charge-type': e});
                  }}
                >
                  <SelectOption
                    label={intl.formatMessage(
                      messages.selectOptionLabelDirectCharge,
                    )}
                    value="direct"
                    key="direct-opt"
                  />
                  <SelectOption
                    label={intl.formatMessage(
                      messages.selectOptionLabelOnBehalfOf,
                    )}
                    value="obo"
                    key="obo-opt"
                  />
                  <SelectOption
                    label={intl.formatMessage(
                      messages.selectOptionLabelNotOnBehalfOf,
                    )}
                    value="notobo"
                    key="notobo-opt"
                  />
                </Select>
                <AlignBox reference={TitleTextProps}>
                  <Box>
                    <InfoTooltip
                      content={
                        <List className="connectTooltipList">
                          <ListItem>
                            <Text weight="bold">
                              {intl.formatMessage(
                                messages.directTooltipHeading,
                              )}
                            </Text>
                            <Text>
                              {intl.formatMessage(messages.directTooltip)}
                            </Text>
                          </ListItem>
                          <ListItem>
                            <Text weight="bold">
                              {intl.formatMessage(
                                messages.onBehalfOfTooltipHeading,
                              )}
                            </Text>
                            <Text>
                              {intl.formatMessage(messages.onBehalfOfTooltip)}
                            </Text>
                          </ListItem>
                          <ListItem>
                            <Text weight="bold">
                              {intl.formatMessage(
                                messages.notOnBehalfOfTooltipHeading,
                              )}
                            </Text>
                            <Text>
                              {intl.formatMessage(
                                messages.notOnBehalfOfTooltip,
                              )}
                            </Text>
                          </ListItem>
                        </List>
                      }
                      position="right"
                      delay={650}
                    />
                  </Box>
                </AlignBox>
              </Group>
            </FormRow>
          </FormBlock>
          {(getPref('connect-charge-type') === 'direct' ||
            !getPref('connect-charge-type')) && (
            <motion.div
              key="integration-select"
              initial="enter"
              animate="center"
              exit="exit"
              custom={direction}
              variants={variants}
              transition={transition}
            >
              <FormBlock rowDividers>
                <FormRow name="accountRow" label="Connected account type:">
                  <Group direction="horizontal" spacing={12}>
                    <Select
                      id="acctType"
                      width="long"
                      name="acctType"
                      value={getPref('connect-account-type') || 'standard'}
                      onChange={(e) => {
                        updatePrefs({...selections, 'connect-account-type': e});
                        setSelections({
                          ...selections,
                          'connect-account-type': e,
                        });
                      }}
                    >
                      <SelectOption
                        label={intl.formatMessage(
                          messages.selectOptionLabelStandardAccount,
                        )}
                        value="standard"
                        key="select-opt"
                      />
                      <SelectOption
                        label={intl.formatMessage(
                          messages.selectOptionLabelCustomAccount,
                        )}
                        value="custom"
                        key="custom-opt"
                      />
                      <SelectOption
                        label={intl.formatMessage(
                          messages.selectOptionLabelExpressAccount,
                        )}
                        value="express"
                        key="express-opt"
                      />
                    </Select>
                    <AlignBox reference={TitleTextProps}>
                      <Box>
                        <InfoTooltip
                          content={
                            <List className="connectTooltipList">
                              <ListItem>
                                <Text weight="bold">
                                  {intl.formatMessage(
                                    messages.standardAccountTooltipHeading,
                                  )}
                                </Text>
                                <Text>
                                  {intl.formatMessage(
                                    messages.standardAccountTooltip,
                                  )}
                                </Text>
                              </ListItem>
                              <ListItem>
                                <Text weight="bold">
                                  {intl.formatMessage(
                                    messages.customAccountTooltipHeading,
                                  )}
                                </Text>
                                <Text>
                                  {intl.formatMessage(
                                    messages.customAccountTooltip,
                                  )}
                                </Text>
                              </ListItem>
                              <ListItem>
                                <Text weight="bold">
                                  {intl.formatMessage(
                                    messages.expressAccountTooltipHeading,
                                  )}
                                </Text>
                                <Text>
                                  {intl.formatMessage(
                                    messages.expressAccountTooltip,
                                  )}
                                </Text>
                              </ListItem>
                            </List>
                          }
                          position="right"
                          delay={650}
                        />
                      </Box>
                    </AlignBox>
                  </Group>
                </FormRow>
              </FormBlock>
            </motion.div>
          )}
        </FormLayout>
      </Card>
    </Group>
  );
};
